/**
 * Pure history over plans: where each one came from, what was decided about it,
 * how many times the owner asked for it again, and what the planner said when it
 * could not draft one. No I/O and no clock access - the caller passes time in.
 */
import { agoLabel } from './runs';
import type { AuditEvent, OwnerCommand, Plan } from './data';

/**
 * The reason recorded against a command the planner answered instead of planning.
 *
 * It is stored on a row whose status is 'failed', because the planner writes one
 * bookkeeping row either way and there is no other status to write. That is a
 * record-keeping detail, and it was leaking into the owner's face: asking Brunel a
 * question put another red "could not be drafted" in the header, so nine questions
 * read as nine failures. A question that got an answer did not fail.
 */
const ANSWERED = 'question_not_a_command';

/**
 * Re-evaluation closes an old held or proposed plan with the existing failed
 * status and this reason. History says expired. It is not a draft that failed.
 */
const STALE = 'stale';

/** Whether this row is a question the planner answered rather than a plan that failed. */
export function wasAnswered(plan: Plan): boolean {
  return plan.status === 'failed' && plan.reason === ANSWERED;
}

/** Whether re-evaluation closed this plan because it was older than a day. */
function expiredAsStale(plan: Plan): boolean {
  return plan.status === 'failed' && plan.reason === STALE;
}

/**
 * The plans that belong in the review the owner opens from "plans to approve".
 *
 * Everything the review offers him is something to decide: approve it, turn it
 * down, or ask for it to be drafted again. An answered question is none of
 * those - it already got its answer, in the same turn of the chat - so it is
 * not in the review at all. It stays in Plan history, which is the record.
 *
 * This is the same rule `planAttention` counts by, applied to the list itself:
 * the control said three and the review showed twelve, nine of them red.
 */
export function reviewablePlans(plans: readonly Plan[]): Plan[] {
  return plans.filter((plan) => !wasAnswered(plan) && !expiredAsStale(plan));
}

/** What happened to a plan, in the words the owner would use. No status codes. */
export function planStatusLabel(plan: Plan): string {
  if (wasAnswered(plan)) return 'answered';
  if (expiredAsStale(plan)) return 'expired';
  switch (plan.status) {
    case 'proposed': return plan.held_for ? 'needs your answer' : 'waiting on you';
    case 'approved': return plan.authority === 'owner_command' ? 'accepted on your instruction' : 'approved';
    case 'rejected': return 'you turned it down';
    default: return 'could not be drafted';
  }
}

export interface PlanHistoryRow {
  readonly id: string;
  readonly projectId: string;
  /** The owner's own words, when the command that started this plan is still in view. */
  readonly command: string;
  readonly status: Plan['status'];
  /** Whether this row is a question Brunel answered rather than a plan that failed. */
  readonly answered: boolean;
  /** What happened to it, in plain words: "waiting on you", "answered", and so on. */
  readonly statusLabel: string;
  readonly summary: string;
  /**
   * What the planner said when it could not draft, otherwise null. An answered
   * question carries a bookkeeping code rather than a reason, and that code is
   * not something to read, so it is left out here.
   */
  readonly reason: string | null;
  /** How many times the owner asked for this plan to be drafted again. */
  readonly retries: number;
  readonly createdAt: string;
  /** "just now", "12 min ago", and so on. */
  readonly createdLabel: string;
}

/** What the plans waiting on the owner add up to, for the control that opens the review. */
export interface PlanAttention {
  /** Plans drafted and waiting for approve or reject. */
  readonly waiting: number;
  /** Plans the planner could not draft, which the owner can ask for again. */
  readonly failed: number;
  /** Questions the planner answered. Counted, never called a failure. */
  readonly answered: number;
  readonly total: number;
  /** What the control says, or "" when there is nothing to review. */
  readonly label: string;
}

/** Counts and wording for the "Plans to approve" control. Plain words, no jargon. */
export function planAttention(plans: readonly Plan[]): PlanAttention {
  const waiting = plans.filter((p) => p.status === 'proposed').length;
  const answered = plans.filter(wasAnswered).length;
  const stale = plans.filter(expiredAsStale).length;
  const failed = plans.filter((p) => p.status === 'failed').length - answered - stale;
  const parts: string[] = [];
  if (waiting > 0) parts.push(waiting === 1 ? '1 plan to approve' : `${waiting} plans to approve`);
  if (failed > 0) parts.push(failed === 1 ? '1 could not be drafted' : `${failed} could not be drafted`);
  // An answered question is not waiting on the owner, so it is not in the total either -
  // the control is for things he has to do something about.
  return { waiting, failed, answered, total: waiting + failed, label: parts.join(' · ') };
}

/** Audit rows that record the owner asking for a plan to be drafted again. */
export function retryCounts(audit: readonly AuditEvent[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const event of audit) {
    if (event.event_type !== 'plan.decided' || event.reason_code !== 'retry') continue;
    const planId = event.payload?.['planId'];
    if (typeof planId !== 'string' || planId === '') continue;
    counts.set(planId, (counts.get(planId) ?? 0) + 1);
  }
  return counts;
}

/** Every plan, newest first, with the command it came from and what happened to it. */
export function planHistory(
  plans: readonly Plan[], commands: readonly OwnerCommand[],
  audit: readonly AuditEvent[], nowMs: number,
): PlanHistoryRow[] {
  const retries = retryCounts(audit);
  const words = new Map(commands.map((c) => [c.id, c.content]));
  return [...plans]
    .sort((a, b) => {
      const left = Date.parse(a.created_at);
      const right = Date.parse(b.created_at);
      if (Number.isNaN(left) && Number.isNaN(right)) return 0;
      if (Number.isNaN(left)) return 1;
      if (Number.isNaN(right)) return -1;
      return right - left;
    })
    .map((plan) => {
      const createdMs = Date.parse(plan.created_at);
      const answered = wasAnswered(plan);
      return {
        id: plan.id,
        projectId: plan.project_id,
        command: words.get(plan.command_id) ?? '',
        status: plan.status,
        answered,
        statusLabel: planStatusLabel(plan),
        summary: plan.summary,
        reason: plan.status === 'failed' && !answered ? plan.reason : null,
        retries: retries.get(plan.id) ?? 0,
        createdAt: plan.created_at,
        createdLabel: Number.isNaN(createdMs) ? '' : agoLabel(nowMs - createdMs),
      };
    });
}


/**
 * How much of the owner's command fits inside the Discuss prefill. Longer is cut
 * with an ellipsis so the composer stays a discussion prompt, not a dump of the plan.
 */
const DISCUSS_COMMAND_LIMIT = 160;

/** The first eight characters of a plan id - enough to recognise it on screen. */
export function shortPlanId(planId: string): string {
  return planId.slice(0, 8);
}

/**
 * Words that go into the command box when the owner chooses Discuss plan rather
 * than approve or reject. This must itself be an unmistakable QUESTION under
 * Brunel's command classifier, so submitting it is answered rather than planned.
 * The pending plan therefore stays pending and no replacement plan is drafted.
 */
export function discussPrefill(command: string): string {
  const trimmed = command.trim();
  const quoted = trimmed === ''
    ? '…'
    : trimmed.length <= DISCUSS_COMMAND_LIMIT
      ? trimmed
      : `${trimmed.slice(0, DISCUSS_COMMAND_LIMIT - 1)}…`;
  return `What assumptions and reasoning are behind the pending plan from my request: "${quoted}"?`;
}

/**
 * What the review shows before Approve, so the owner consents with the right facts
 * in front of him: what he asked, where the plan came from, and that nothing has run.
 */
export interface PlanReviewModel {
  /** The owner's exact originating command, or empty when that command is out of view. */
  readonly youAsked: string;
  readonly projectName: string;
  readonly createdLabel: string;
  readonly shortPlanId: string;
  readonly planId: string;
  /** Always "Brunel planning system" (model-drafted and fallbackPlan alike). */
  readonly source: string;
  readonly taskCount: number;
  /** "1 task" / "3 tasks" - ready for the provenance line. */
  readonly taskCountLabel: string;
  /**
   * Provenance facts in reading order. Created time is omitted when the timestamp
   * is unusable, rather than inventing a label.
   */
  readonly provenanceLines: readonly string[];
  /** Shown on every proposed plan: nothing has executed yet. */
  readonly nothingRanYet: string;
  /** Prefill for Discuss plan - non-mutating discussion; plan stays pending. */
  readonly discussPrefill: string;
}

/** Build the consent copy for a proposed plan. Pure: the caller passes time in. */
export function planReviewModel(input: {
  readonly plan: Plan;
  readonly projectName: string;
  readonly command: string;
  readonly nowMs: number;
}): PlanReviewModel {
  const { plan, projectName, command, nowMs } = input;
  const createdMs = Date.parse(plan.created_at);
  const createdLabel = Number.isNaN(createdMs) ? '' : agoLabel(nowMs - createdMs);
  const idShort = shortPlanId(plan.id);
  const taskCount = plan.tasks.length;
  const taskCountLabel = taskCount === 1 ? '1 task' : `${taskCount} tasks`;
  const source = 'Brunel planning system';
  const provenanceLines: string[] = [
    `Project · ${projectName}`,
  ];
  if (createdLabel !== '') provenanceLines.push(`Created · ${createdLabel}`);
  provenanceLines.push(`Plan · ${idShort}`);
  provenanceLines.push(`Source · ${source}`);
  provenanceLines.push(taskCountLabel);

  return {
    youAsked: command,
    projectName,
    createdLabel,
    shortPlanId: idShort,
    planId: plan.id,
    source,
    taskCount,
    taskCountLabel,
    provenanceLines,
    nothingRanYet: 'Nothing in this plan has run yet.',
    discussPrefill: discussPrefill(command),
  };
}
